import React, { useEffect, useState } from "react";
import Select from "react-select";

import { supabase, TABLE } from './constants';
import { getPlayerComparison, fetchTennisAbstractStats, probVictoire } from './utils';
import PlayerProfiles from './components/PlayerProfiles';
import PlayerComparison from './components/PlayerComparison';
import UpcomingMatches from './components/UpcomingMatches';
import Navigation from './components/Navigation';

export default function App() {
  const [players, setPlayers] = useState([]);
  const [selected1, setSelected1] = useState(null);
  const [selected2, setSelected2] = useState(null);
  const [activeTab, setActiveTab] = useState("probabilities");
  const [comparisonResults, setComparisonResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeSection, setActiveSection] = useState("comparison");
  
  // Global state for AI summaries - persists across tab switches
  const [aiSummaries, setAiSummaries] = useState({});

  const [stats1, setStats1] = useState(null);
  const [stats2, setStats2] = useState(null);
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);

  // Function to get/set AI summaries
  const getAiSummaryKey = (player1, player2, surface) => {
    if (!player1 || !player2 || !surface) return null;
    return `${player1.player}_vs_${player2.player}_${surface}`;
  };

  const getAiSummary = (player1, player2, surface) => {
    const key = getAiSummaryKey(player1, player2, surface);
    return key ? aiSummaries[key] : null;
  };

  const setAiSummary = (player1, player2, surface, summary) => {
    const key = getAiSummaryKey(player1, player2, surface);
    if (key) {
      setAiSummaries(prev => ({
        ...prev,
        [key]: summary
      }));
    }
  };

  useEffect(() => {
    const fetchPlayers = async () => {
      const { data, error } = await supabase.from(TABLE).select("*");
      if (error) {
        console.error("Error fetching players:", error);
      } else if (data) {
        setPlayers(data.sort((a, b) => a.player.localeCompare(b.player)));
      }
    };
    fetchPlayers();
  }, []);
  
  useEffect(() => {
    if (selected1 && selected1.tennis_abstract_name) {
      setLoading1(true);
      setStats1({ message: "Scraping en cours..." });
      fetchTennisAbstractStats(selected1.tennis_abstract_name)
        .then(data => {
          setStats1(data);
        })
        .catch(error => {
          setStats1({ message: "Erreur lors du scraping : " + (error?.message || "inconnue") });
        })
        .finally(() => setLoading1(false));
    } else {
      setStats1(null);
    }
  }, [selected1]);

  useEffect(() => {
    if (selected2 && selected2.tennis_abstract_name) {
      setLoading2(true);
      setStats2({ message: "Scraping en cours..." });
      fetchTennisAbstractStats(selected2.tennis_abstract_name)
        .then(data => {
          setStats2(data);
        })
        .catch(error => {
          setStats2({ message: "Erreur lors du scraping : " + (error?.message || "inconnue") });
        })
        .finally(() => setLoading2(false));
    } else {
      setStats2(null);
    }
  }, [selected2]);
  
  // Calculation of advanced probabilities when both players are selected
  useEffect(() => {
    if (selected1 && selected2) {
      const calculateAll = async () => {
        setIsCalculating(true);
        try {
          const hardComp = await getPlayerComparison(selected1, selected2, 'Hard');
          const clayComp = await getPlayerComparison(selected1, selected2, 'Clay');
          const grassComp = await getPlayerComparison(selected1, selected2, 'Grass');
          
          setComparisonResults({
            hard: hardComp,
            clay: clayComp,
            grass: grassComp
          });
        } catch (error) {
          console.error("Error calculating probabilities:", error);
        } finally {
          setIsCalculating(false);
        }
      };
      
      calculateAll();
    } else {
      setComparisonResults(null);
    }
  }, [selected1, selected2]);
  
  // Prepare options for react-select
  const options = players.map(j => ({
    value: j.player,
    label: j.player,
    ...j,
    tennis_abstract_name: j.player_slug // Using player_slug as tennis_abstract_name
  }));

  const customStyles = {
    container: (provided) => ({
      ...provided,
      minWidth: 200,
      width: 230
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 100
    })
  };

  // Function to render confidence indicator
  const renderConfidence = (confidence) => {
    let color = '';
    let label = '';
    
    switch(confidence) {
      case 'high':
        color = 'green';
        label = 'Élevée';
        break;
      case 'medium-high':
        color = '#5cb85c';
        label = 'Assez élevée';
        break;
      case 'medium':
        color = '#f0ad4e';
        label = 'Moyenne';
        break;
      case 'medium-low':
        color = '#f0ad4e';
        label = 'Assez faible';
        break;
      case 'low':
        color = '#d9534f';
        label = 'Faible';
        break;
      default:
        color = '#777';
        label = 'Inconnue';
    }
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '12px',
        backgroundColor: color,
        color: 'white'
      }}>
        {label}
      </span>
    );
  };
  
  // Fonction pour analyser un match à partir de l'API Sportradar ou des matchs à venir
  const handleMatchSelect = (player1, player2) => {
    // On vérifie si les données des joueurs sont déjà au format attendu
    // (cas où elles viennent directement de URLs)
    if (player1 && player2 && typeof player1 === 'object' && typeof player2 === 'object') {
      // Si les joueurs sont déjà des objets complets (venant des URLs directes)
      // On les adapte au format attendu par l'application
      const player1Data = {
        value: player1.player,
        label: player1.player,
        ...player1,
        tennis_abstract_name: player1.player_slug
      };
      
      const player2Data = {
        value: player2.player,
        label: player2.player,
        ...player2,
        tennis_abstract_name: player2.player_slug
      };
      
      setSelected1(player1Data);
      setSelected2(player2Data);
      setActiveSection("comparison");
      setActiveTab("probabilities");
      return;
    }
    
    // Sinon, on utilise la recherche par nom comme avant (pour les cas de SportRadar ou legacy)
    const findPlayer = (playerName) => {
      return options.find(player => player.label === playerName);
    };
    
    const player1Data = typeof player1 === 'object' ? player1 : findPlayer(player1);
    const player2Data = typeof player2 === 'object' ? player2 : findPlayer(player2);
    
    if (player1Data && player2Data) {
      setSelected1(player1Data);
      setSelected2(player2Data);
      setActiveSection("comparison");
      setActiveTab("probabilities");
    } else {
      const missingPlayers = [];
      if (!player1Data) missingPlayers.push(typeof player1 === 'object' ? player1.player : player1);
      if (!player2Data) missingPlayers.push(typeof player2 === 'object' ? player2.player : player2);
      
      alert(`Impossible d'analyser ce match. Les joueurs suivants n'ont pas été trouvés dans notre base de données: ${missingPlayers.join(', ')}`);
    }
  };
  
  // Fonction de gestion des matchs Sportradar
  const handleSportradarMatchSelect = (match) => {
    // Rechercher les joueurs dans notre base de données
    const findPlayerByName = async (playerName) => {
      const lastName = playerName.split(' ').pop();
      
      const { data } = await supabase
        .from(TABLE)
        .select("*")
        .ilike('player', `%${lastName}%`)
        .limit(1);
      
      if (data && data.length > 0) {
        const playerData = data[0];
        return {
          value: playerData.player,
          label: playerData.player,
          ...playerData,
          tennis_abstract_name: playerData.player_slug
        };
      }
      
      return null;
    };
    
    // Fonction asynchrone pour trouver les deux joueurs et les analyser
    const findPlayersAndAnalyze = async () => {
      try {
        const homePlayer = await findPlayerByName(match.home_team);
        const awayPlayer = await findPlayerByName(match.away_team);
        
        if (homePlayer && awayPlayer) {
          setSelected1(homePlayer);
          setSelected2(awayPlayer);
          setActiveSection("comparison");
          setActiveTab("probabilities");
        } else {
          const missingPlayers = [];
          if (!homePlayer) missingPlayers.push(match.home_team);
          if (!awayPlayer) missingPlayers.push(match.away_team);
          
          alert(`Impossible d'analyser ce match. Les joueurs suivants n'ont pas été trouvés dans notre base de données: ${missingPlayers.join(', ')}`);
        }
      } catch (error) {
        console.error("Erreur lors de la recherche des joueurs:", error);
        alert("Erreur lors de la recherche des joueurs dans notre base de données");
      }
    };
    
    findPlayersAndAnalyze();
  };

  return (
    <div style={{
      maxWidth: 800, margin: "60px auto", fontFamily: "Arial, sans-serif",
      background: "#fff", padding: 32, borderRadius: 16, boxShadow: "0 0 24px #ddd"
    }}>
      <h2 style={{textAlign: "center", marginBottom: 20}}>Tennis Elo Analyzer</h2>
      
      <Navigation 
        activeSection={activeSection} 
        onSectionChange={setActiveSection} 
      />
      
      {activeSection === "comparison" && (
        <>
          <h3 style={{textAlign: "center", marginBottom: 20}}>Comparer 2 joueurs ATP (Elo)</h3>
          <div style={{display: "flex", justifyContent: "center", gap: 10, marginBottom: 16, alignItems: "center"}}>
            <Select
              options={options}
              value={selected1}
              onChange={setSelected1}
              placeholder="Choisir joueur 1"
              styles={customStyles}
              isClearable
              isSearchable
            />
            <span style={{fontWeight: 600, fontSize: 18}}>VS</span>
            <Select
              options={options}
              value={selected2}
              onChange={setSelected2}
              placeholder="Choisir joueur 2"
              styles={customStyles}
              isClearable
              isSearchable
            />
          </div>
          
          {(selected1 && selected2) && (
            <div style={{
              border: "1px solid #eee", borderRadius: 12, padding: 20, marginTop: 24,
              background: "#fafbfc"
            }}>
              {/* Navigation par onglets */}
              <div style={{ display: "flex", borderBottom: "1px solid #eee", marginBottom: 20 }}>
                <button 
                  onClick={() => setActiveTab("probabilities")}
                  style={{
                    padding: "10px 15px",
                    background: activeTab === "probabilities" ? "#f0f0f0" : "transparent",
                    border: "none",
                    borderBottom: activeTab === "probabilities" ? "2px solid #3498db" : "none",
                    cursor: "pointer",
                    fontWeight: activeTab === "probabilities" ? "bold" : "normal"
                  }}
                >
                  Probabilités
                </button>
                <button 
                  onClick={() => setActiveTab("details")}
                  style={{
                    padding: "10px 15px",
                    background: activeTab === "details" ? "#f0f0f0" : "transparent",
                    border: "none",
                    borderBottom: activeTab === "details" ? "2px solid #3498db" : "none",
                    cursor: "pointer",
                    fontWeight: activeTab === "details" ? "bold" : "normal"
                  }}
                >
                  Profils joueurs
                </button>
              </div>
              
              {/* Contenu de l'onglet Probabilités */}
              {activeTab === "probabilities" && (
                <PlayerComparison 
                  selected1={selected1}
                  selected2={selected2}
                  comparisonResults={comparisonResults}
                  isCalculating={isCalculating}
                  renderConfidence={renderConfidence}
                  getAiSummary={getAiSummary}
                  setAiSummary={setAiSummary}
                />
              )}
              
              {/* Contenu de l'onglet Profils joueurs */}
              {activeTab === "details" && (
                <PlayerProfiles 
                  selected1={selected1}
                  selected2={selected2}
                  stats1={stats1}
                  stats2={stats2}
                  loading1={loading1}
                  loading2={loading2}
                />
              )}
            </div>
          )}
        </>
      )}
      
      {activeSection === "upcomingMatches" && (
        <UpcomingMatches onAnalyzeMatch={handleMatchSelect} />
      )}
    </div>
  );
}